<template>
  <v-list-item
    :disabled="disabled"
    density="compact"
    data-test="apply-content-language-action"
    @click="showDialog = true"
  >
    <template v-slot:prepend>
      <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-translate</v-icon>
    </template>
    <v-list-item-title class="text-body-2 text-medium-emphasis">Apply Content Language</v-list-item-title>
    <v-list-item-subtitle v-if="jobStore.isRunning" class="text-caption" data-test="apply-content-language-running">
      A job is already running
    </v-list-item-subtitle>
  </v-list-item>

  <div v-if="startError" class="px-3 pb-2 text-caption text-error" data-test="apply-content-language-error">
    {{ startError }}
  </div>

  <v-dialog v-model="showDialog" max-width="560" data-test="apply-content-language-dialog">
    <v-card rounded="lg">
      <v-card-title class="text-subtitle-1 d-flex align-center">
        <v-icon size="small" class="mr-2" color="primary">mdi-translate</v-icon>
        Apply Content Language
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <div class="text-body-2 mb-3">
          Every vehicle in this account will be brought into the account's content languages.
        </div>

        <div class="mb-1 text-body-2" data-test="apply-dialog-description-language">
          <strong>Descriptions:</strong> {{ getLanguageLabel(targets.descriptionLanguage) }}
        </div>
        <div class="mb-4 text-body-2" data-test="apply-dialog-highlight-language">
          <strong>Highlights:</strong> {{ getLanguageLabel(targets.highlightLanguage) }}
        </div>

        <ul class="text-body-2 text-medium-emphasis pl-5">
          <li>Existing content may be translated, restored from a saved version, or generated.</li>
          <li>Locked descriptions are skipped.</li>
          <li>Highlights are processed independently of descriptions and are never locked.</li>
          <li>The job continues in the background; you can keep working.</li>
        </ul>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" data-test="apply-content-language-cancel" @click="showDialog = false">Cancel</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="jobStore.starting"
          :disabled="disabled"
          data-test="apply-content-language-confirm"
          @click="confirm"
        >
          Apply
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/**
 * The Apply Content Language action.
 *
 * Sits with the account's language settings rather than in an editor tab,
 * because it acts on the whole account rather than on the vehicle being edited.
 *
 * App.vue owns the shared Content Language capability boundary. The service
 * authorizes every request independently, and a 403 is reported rather than
 * assumed away.
 *
 * The confirmation states the languages that will actually be applied, with
 * Same as Description already resolved, and deliberately quotes no vehicle
 * count: the service does not know one until it has planned the job, and a
 * number invented here would be a guess presented as a fact.
 */
import { computed, ref } from 'vue'

import { getLanguageLabel, resolveTargetLanguages } from '../contentLanguage'
import { describeStartFailure } from '../languageJob'
import { useLanguageJobStore } from '../stores/languageJob'
import { useProductSettings } from '../useProductSettings'

const jobStore = useLanguageJobStore()
const { settings } = useProductSettings()

const showDialog = ref(false)

/** Resolved, so Same as Description is shown as the language it resolves to. */
const targets = computed(() => resolveTargetLanguages(settings.value))

const disabled = computed(() => jobStore.starting || jobStore.isRunning)

const startError = computed(() => describeStartFailure(jobStore.startFailure))

/**
 * The store refuses a second submission while one is in flight, so a double
 * click cannot start two jobs even if the disabled state has not caught up.
 * The dialog closes only once the request has been answered, so the user is
 * not left thinking a rejected start succeeded.
 */
const confirm = async () => {
  const started = await jobStore.start()
  if (started) showDialog.value = false
}
</script>
